import { providers } from "@tripian/core";
import Model, { Providers } from "@tripian/model";
import { useCallback, useState } from "react";
import adyen from "adyen-cse-js";
import moment from "moment";
import useReservation from "./useReservation";
import useTranslate from "./useTranslate";
import { useDispatch } from "react-redux";
import { saveNotification } from "../redux/action/user";

export type GygLoader = {
  tourCatalogLoader: boolean;
  tourInfoLoader?: boolean;
  tourDataLoader?: boolean;
  tourDetailsLoader?: boolean;
  bookingLoader?: boolean;
  paymentLoader?: boolean;
};

export const useGygApi = (cityId?: number, cityName?: string) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [gygLoaders, setGygLoaders] = useState<GygLoader>({
    tourCatalogLoader: false,
  });
  const dispatch = useDispatch();
  const [gygTourOptionDetails, setGygTourOptionDetails] = useState<
    Array<{
      option: Providers.Gyg.TourOption;
      availabilities: Providers.Gyg.TourAvailabilityEx[];
      pricings?: Providers.Gyg.TourOptionPricing;
    }>
  >();
  const [gygTourOptions, setGygTourOptions] =
    useState<Providers.Gyg.TourOptionNew[]>();
  const [gygTourPriceBreakdown, setGygTourPriceBreakdown] =
    useState<Providers.Gyg.TourPriceBreakdown>();
  const [gygBookingInfo, setGygBookingInfo] = useState<
    Providers.Gyg.TourBooking | undefined
  >();
  // const [gygTourData, setGygTourData] = useState<Providers.Gyg.TourData | undefined>(undefined);

  const [gygTourData, setGygTourData] = useState<
    Providers.Gyg.TourData | undefined
  >(undefined);

  const [successfullBooking, setSuccessfullBooking] = useState<boolean>(false);

  const { reservationAdd } = useReservation(cityId);

  const { t } = useTranslate();

  const resetBookingState = () => {
    setGygTourOptionDetails(undefined);
    setGygBookingInfo(undefined);
  };

  const fetchGygTourInfo = useCallback(async (bookingId: string) => {
    setGygLoaders(prev => ({ ...prev, tourDataLoader: true }));
    setGygTourData(undefined);

    if (window.tconfig.SHOW_TOURS_AND_TICKETS && providers.gyg) {
      if (
        window.tconfig.TOUR_TICKET_PROVIDER_IDS.some(
          x => x === Model.PROVIDER_ID.GYG
        )
      ) {
        try {
          const data = await providers.gyg.tourData(Number(bookingId));
          setGygTourData(data);
          return true;
        } catch (gygFetchTourDataError) {
          dispatch(
            saveNotification(
              Model.NOTIFICATION_TYPE.ERROR,
              "gygFetchTourData",
              gygFetchTourDataError as string
            )
          );
          return false;
        } finally {
          setGygLoaders(prev => ({ ...prev, tourDataLoader: false }));
        }
      }
    }

    return Promise.resolve(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTourOptionsWithDetails = async (
    tourId: number,
    startDate: string,
    endDate: string,
    adult: number,
    children?: number
  ) => {
    setGygLoaders({ ...gygLoaders, tourDetailsLoader: true });
    const totalTravellerCount = adult + (children || 0);

    resetBookingState();

    const newGygTourInfoDetails: Array<{
      option: Providers.Gyg.TourOption;
      availabilities: Providers.Gyg.TourAvailabilityEx[];
      pricings?: Providers.Gyg.TourOptionPricing;
    }> = [];

    if (providers.gyg) {
      const tourAvailabilities: Providers.Gyg.TourAvailabilityEx[] =
        await providers.gyg.tourAvailabilities(
          tourId,
          moment(startDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
          moment(endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss")
        );

      if (tourAvailabilities.length > 0) {
        const tourOptions: Providers.Gyg.TourOption[] =
          await providers.gyg.options(tourId);

        const tourOptionPricingPromises: Promise<Providers.Gyg.TourOptionPricing>[] =
          [];

        tourOptions.forEach(tourOption => {
          const tourOptionsResponse = providers.gyg!.optionPricings(
            tourOption.option_id
          );

          tourOptionPricingPromises.push(tourOptionsResponse);
          newGygTourInfoDetails.push({
            option: tourOption,
            availabilities: [],
          });
        });

        await Promise.all(tourOptionPricingPromises)
          .then(tourOptionPricingResponse => {
            newGygTourInfoDetails.forEach((info, index) => {
              // console.log('index:', index);
              const currentOptionPricing = tourOptionPricingResponse[index];

              if (
                currentOptionPricing.total_minimum_participants <=
                totalTravellerCount
              ) {
                // console.log('currentOptionPricing:', currentOptionPricing);
                const currentAvailabilities = tourAvailabilities.filter(
                  ta => ta.pricing_id === currentOptionPricing.pricing_id
                );

                // console.log('currentAvailabilities', currentAvailabilities);
                newGygTourInfoDetails[index].pricings = currentOptionPricing;
                newGygTourInfoDetails[index].availabilities.push(
                  ...currentAvailabilities
                );
              }
            });
          })
          .catch(gygFetchOptionDetailsError => {
            dispatch(
              saveNotification(
                Model.NOTIFICATION_TYPE.ERROR,
                "gygFetchOptionDetails",
                "Something went wrong"
              )
            );
          })
          .finally(() => {
            setGygLoaders({ ...gygLoaders, tourDetailsLoader: false });
          });

        const availablePricings = newGygTourInfoDetails.filter(
          pricings => pricings.availabilities.length > 0
        );

        setGygTourOptionDetails(availablePricings);
      } else {
        setGygLoaders({ ...gygLoaders, tourDetailsLoader: false });
        setGygTourOptionDetails([]);
      }

      /**
       * TEST providers.gyg.tourData
       */
      // providers.gyg.tourData(tourId, "2023-01-26T00:00:00", "2023-01-28T23:59:59");
    }
  };

  const gygTourBooking = async (
    bookingRequest: Providers.Gyg.TourBookingRequest
  ): Promise<boolean> => {
    setGygLoaders({
      ...gygLoaders,
      tourDetailsLoader: false,
      bookingLoader: true,
    });

    try {
      if (providers.gyg) {
        const res = await providers.gyg.bookingAdd(bookingRequest);
        if ((res as any).status === "ERROR" && (res as any).errors) {
          const errorMessage = (res as any).errors[0].errorMessage;
          dispatch(
            saveNotification(
              Model.NOTIFICATION_TYPE.ERROR,
              "gygTourBooking",
              errorMessage
            )
          );
          return false;
        } else if (
          (res as any)._metadata &&
          (res as any)._metadata.status === "OK" &&
          (res as any).data
        ) {
          setGygBookingInfo((res as any).data);
          return true;
        } else {
          dispatch(
            saveNotification(
              Model.NOTIFICATION_TYPE.ERROR,
              "gygTourBooking",
              t("trips.toursAndTickets.error.unexpectedResponse")
            )
          );
          return false;
        }
      } else {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "gygTourBooking",
            "Something went wrong"
          )
        );
        return false;
      }
    } catch (error) {
      dispatch(
        saveNotification(
          Model.NOTIFICATION_TYPE.ERROR,
          "gygTourBooking",
          error as string
        )
      );
      return false;
    } finally {
      setGygLoaders({
        ...gygLoaders,
        tourDetailsLoader: false,
        bookingLoader: false,
      });
    }
  };

  const fetchGygTourOptions = useCallback(
    (tourId: number, startDate: string, endDate?: string): Promise<Boolean> => {
      setGygLoaders({ ...gygLoaders, tourDetailsLoader: true });
      if (window.tconfig.SHOW_TOURS_AND_TICKETS && providers.gyg) {
        if (
          window.tconfig.TOUR_TICKET_PROVIDER_IDS.some(
            x => x === Model.PROVIDER_ID.GYG
          )
        ) {
          return providers.gyg
            .tourOptions(
              tourId,
              moment(startDate).format("YYYY-MM-DDThh:mm:ss"),
              endDate
                ? moment(endDate).format("YYYY-MM-DDThh:mm:ss")
                : undefined
            )
            .then(data => {
              setGygTourOptions(data.data.tour_options);
              return true;
            })
            .catch(gygFetchTourOptionsError => {
              // console.log("Get Your Guide Tour Options Request Error",gygFetchTourOptionsError)
              dispatch(
                saveNotification(
                  Model.NOTIFICATION_TYPE.ERROR,
                  "gygFetchTourOptions",
                  gygFetchTourOptionsError as string
                )
              );
              return false;
            })
            .finally(() => {
              setGygLoaders({ ...gygLoaders, tourDetailsLoader: false });
            });
        }
      }

      return Promise.resolve(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gygLoaders]
  );

  const fetchGygTourPriceBreakdown = useCallback(
    (
      tourId: number,
      tourOptionsRequest: Providers.Gyg.TourPriceBreakdownRequest
    ) => {
      setGygLoaders({ ...gygLoaders, tourDetailsLoader: true });

      if (providers.gyg) {
        providers.gyg
          ?.tourPriceBreakdown(tourId, tourOptionsRequest)
          .then((res: Providers.Gyg.TourPriceBreakdown) => {
            setGygTourPriceBreakdown(res);
          })
          .catch(gygTourOptionsRequestError => {
            // console.log("Get Your Guide Tour Price Breakdown Request Error",gygTourOptionsRequestError)
            setErrorMessage(gygTourOptionsRequestError as string);
          })
          .finally(() => {
            setGygLoaders({ ...gygLoaders, tourDetailsLoader: false });
          });
      } else {
        setErrorMessage("something went wrong");
        setGygLoaders({ ...gygLoaders, tourDetailsLoader: false });
      }
    },
    [gygLoaders]
  );

  const paymentConfiguration = async (): Promise<string | null> => {
    setGygLoaders({ ...gygLoaders, paymentLoader: true });

    try {
      if (!providers.gyg) {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "gygPaymentConfig",
            "GYG provider not available"
          )
        );
        setGygLoaders({ ...gygLoaders, paymentLoader: false });
        return null;
      }

      const result = await providers.gyg.paymentConfiguration();

      if (
        !result ||
        !Array.isArray(result) ||
        result.length === 0 ||
        !result[0] ||
        !result[0].public_key
      ) {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "gygPaymentConfig",
            "Invalid payment configuration response"
          )
        );
        setGygLoaders({ ...gygLoaders, paymentLoader: false });
        return null;
      }

      return result[0].public_key;
    } catch (error) {
      dispatch(
        saveNotification(
          Model.NOTIFICATION_TYPE.ERROR,
          "gygPaymentConfig",
          error instanceof Error
            ? error.message
            : "Payment configuration failed"
        )
      );
      setGygLoaders({ ...gygLoaders, paymentLoader: false });
      return null;
    }
  };

  const confirmPayment = async (
    data: Providers.Gyg.TourShoppingFormData,
    onPaymentError?: () => void
  ) => {
    const publicKey = await paymentConfiguration();

    if (!publicKey) {
      // Payment configuration hatası - modal gösterilecek
      if (onPaymentError) {
        onPaymentError();
      }
      return;
    }

    const newCreditCard = { ...data.creditCard };
    newCreditCard.generationtime = new Date().toISOString();

    // Pass it through the Adyen library
    const cseInstance = adyen.createEncryption(publicKey, {});
    // encrypted payment data
    const encryptedData: string = cseInstance.encrypt(newCreditCard);

    const cartRequest: Providers.Gyg.TourBookingCartRequest = {
      base_data: {
        cnt_language: "en",
        currency: "USD",
      },
      data: {
        shopping_cart: {
          shopping_cart_id: gygBookingInfo?.bookings.shopping_cart_id || 0,
          billing: {
            salutation_code: "m",
            first_name: data.user.first_name,
            last_name: data.user.last_name,
            email: data.user.email,
            is_company: false,
            company_name: "Tripian",
            invoice: false,
            address_line_1: data.user.address_line_1,
            address_line_2: "",
            city: data.user.city,
            postal_code: data.user.postal_code,
            state: "California",
            country_code: data.user.country_code,
            phone_number: data.user.phone_number,
          },
          traveler: {
            salutation_code: "m",
            first_name: data.user.first_name,
            last_name: data.user.last_name,
            email: data.user.email,
            phone_number: data.user.phone_number,
          },
          payment: {
            encrypted_credit_card: {
              format: "adyen",
              data: encryptedData,
            },
          },
        },
      },
    };

    try {
      const reservationInfo = await providers.gyg?.cartAdd(cartRequest);

      setGygBookingInfo(undefined);
      if (reservationInfo && gygTourData) {
        const saveData: Providers.Gyg.TourReservationDetails = {
          data: {
            shopping_cart: {
              ...reservationInfo,
              city_name: cityName || "",
              tour_image: gygTourData?.tour.pictures[0].url || "",
              tour_name: gygTourData?.tour.title || "",
            },
          },
        };

        const formattedBookingDateTime = moment(
          reservationInfo.bookings[0].bookable.datetime
        ).format("YYYY-MM-DD HH:mm:ss");

        await reservationAdd(
          {
            key: Model.PROVIDER_NAME.GYG,
            provider: Model.PROVIDER_NAME.GYG,
            value: saveData,
            bookingDateTime: formattedBookingDateTime,
          },
          false
        );

        setGygLoaders({ ...gygLoaders, paymentLoader: false });
        setSuccessfullBooking(true);
      } else {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "gygReservation",
            "Invalid reservation response"
          )
        );
        setGygLoaders({ ...gygLoaders, paymentLoader: false });
      }
    } catch (gygReservationError) {
      dispatch(
        saveNotification(
          Model.NOTIFICATION_TYPE.ERROR,
          "gygReservation",
          "Reservation failed"
        )
      );
      setGygLoaders({ ...gygLoaders, paymentLoader: false });
    }
  };

  return {
    errorMessage,
    gygLoaders,
    gygTourOptionDetails,
    gygBookingInfo,
    gygTourData,
    confirmPayment,
    fetchTourOptionsWithDetails,
    gygTourBooking,
    fetchGygTourInfo,
    successfullBooking,
    fetchGygTourOptions,
    fetchGygTourPriceBreakdown,
    gygTourOptions,
    gygTourPriceBreakdown,
  };
};
